// ============================================
// Transaction History Page - New Style
// ============================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { 
  FaArrowLeft, 
  FaHistory, 
  FaHome, 
  FaChartBar, 
  FaEye, 
  FaFileAlt,
  FaCoins,
  FaMoneyBillAlt,
  FaExchangeAlt,
  FaChartLine
} from 'react-icons/fa';
import api from '../services/api';
import './History.css';

const TransactionHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const navigate = useNavigate();

  const tabs = [
    { id: 'all', label: 'ทั้งหมด' },
    { id: 'deposit', label: 'ฝากเงิน' },
    { id: 'withdraw', label: 'ถอนเงิน' },
    { id: 'transfer', label: 'โอนเงิน' },
    { id: 'trade', label: 'ประวัติการเทรด' },
  ];

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const [transactionData, tradeData] = await Promise.all([
        api.getTransactionHistory(),
        api.getTradeHistory().catch(() => []) // Handle error if endpoint doesn't exist
      ]);
      
      // Sort by createdAt descending (newest first), then reverse to show newest at bottom
      const sortedTransactions = transactionData.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      ).reverse();
      
      const sortedTrades = (tradeData || []).sort((a, b) => {
        const dateA = new Date(a.completedAt || a.createdAt);
        const dateB = new Date(b.completedAt || b.createdAt);
        return dateB - dateA;
      }).reverse();
      
      setTransactions(sortedTransactions);
      setTrades(sortedTrades);
    } catch (error) {
      toast.error('ไม่สามารถโหลดประวัติได้');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'รอดำเนินการ', class: 'badge-warning' },
      approved: { text: 'อนุมัติแล้ว', class: 'badge-success' },
      rejected: { text: 'ปฏิเสธ', class: 'badge-danger' },
      completed: { text: 'สำเร็จ', class: 'badge-success' }
    };
    return badges[status] || { text: status, class: 'badge-secondary' };
  };

  const getTypeText = (type) => {
    const types = {
      deposit: 'ฝากเงิน',
      withdraw: 'ถอนเงิน',
      transfer: 'โอนเงิน',
      trade: 'ประวัติการเทรด'
    };
    return types[type] || type;
  };

  const getTypeIcon = (type) => {
    const icons = {
      deposit: <FaCoins size={24} />,
      withdraw: <FaMoneyBillAlt size={24} />,
      transfer: <FaExchangeAlt size={24} />,
      trade: <FaChartLine size={24} />
    };
    return icons[type] || <FaHistory size={24} />;
  };

  const getFilteredData = () => {
    if (activeTab === 'all') {
      // Combine transactions and trades, sort by date (oldest first, newest at bottom)
      const allData = [
        ...transactions.map(t => ({ ...t, dataType: 'transaction' })),
        ...trades.map(t => ({ ...t, dataType: 'trade', type: 'trade' }))
      ].sort((a, b) => {
        const dateA = new Date(a.completedAt || a.createdAt);
        const dateB = new Date(b.completedAt || b.createdAt);
        return dateA - dateB; // Oldest first
      });
      return allData;
    } else if (activeTab === 'trade') {
      return trades.map(t => ({ ...t, dataType: 'trade', type: 'trade' }))
        .sort((a, b) => {
          const dateA = new Date(a.completedAt || a.createdAt);
          const dateB = new Date(b.completedAt || b.createdAt);
          return dateA - dateB;
        });
    } else {
      return transactions
        .filter(t => t.type === activeTab)
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    }
  };


  // สรุปข้อมูลเทรด
  const tradeWin = trades.filter(t => t.result === 'win');
  const tradeLoss = trades.filter(t => t.result === 'loss');
  const totalTradeAmount = trades.reduce((sum, t) => sum + (typeof t.amount === 'number' ? t.amount : 0), 0);
  const totalTradeProfit = tradeWin.reduce((sum, t) => {
    if (typeof t.amount === 'number' && typeof t.profitPercent === 'number') {
      return sum + (t.amount * t.profitPercent / 100);
    }
    return sum;
  }, 0);
  const totalTradeCount = trades.length;
  const winCount = tradeWin.length;
  const lossCount = tradeLoss.length;
  const avgProfit = winCount > 0 ? (totalTradeProfit / winCount) : 0;
  const totalProfitPercent = totalTradeAmount > 0 ? (totalTradeProfit / totalTradeAmount) * 100 : 0;

  const filteredData = getFilteredData();

  return (
    <div className="history-page">
      {/* Header */}
      <div className="history-header">
        <button className="btn-back" onClick={() => navigate('/profile')}>
          <FaArrowLeft />
        </button>
        <h1>ประวัติธุรกรรม</h1>
        <div className="header-spacer"></div>
      </div>

      {/* Tabs */}
      <div className="history-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="history-content">
        {/* สรุปข้อมูลเทรด เฉพาะ tab ประวัติการเทรด */}
        {/* ...ไม่มี summary รวมด้านบนอีกต่อไป... */}
        {/* ลบ box ว่างด้านบนสุดของ tab ประวัติการเทรด */}
        {loading ? (
          <div className="loading-container">
            <div className="spinner" />
          </div>
        ) : filteredData.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <FaHistory />
            </div>
            <p>ยังไม่มีประวัติ{activeTab === 'trade' ? 'การเทรด' : 'ธุรกรรม'}</p>
            <small>เมื่อมีการ{activeTab === 'trade' ? 'เทรด' : 'ทำธุรกรรม'} จะแสดงที่นี่</small>
          </div>
        ) : (
          <div className="transactions-list">
            {filteredData.map((item, index) => {
              if (item.dataType === 'trade') {
                // Render trade item
                const tradeStatusBadge = item.result === 'win' 
                  ? { text: 'ชนะ', class: 'badge-success' }
                  : item.result === 'loss'
                  ? { text: 'แพ้', class: 'badge-danger' }
                  : { text: 'รอดำเนินการ', class: 'badge-warning' };
                const profitDisplay = item.result === 'win' && item.profitPercent 
                  ? `+${item.profitPercent.toFixed(2)}%`
                  : item.result === 'loss'
                  ? '-'
                  : '-';
                // ดีไซน์ใหม่ตามที่ผู้ใช้ต้องการ
                return (
                  <motion.div
                    key={`trade-${item.id}`}
                    className="trade-summary-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    style={{
                      background: '#fafafa',
                      borderRadius: '16px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      padding: '18px 28px',
                      marginBottom: '16px',
                      minHeight: '80px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 17, color: '#222' }}>
                      เทรด {item.asset || item.assetId || 'N/A'}
                    </div>
                    <div style={{ fontSize: 13, color: '#888' }}>
                      {new Date(item.completedAt || item.createdAt).toLocaleDateString('th-TH', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    <div style={{ fontSize: 13, color: '#888' }}>
                      {item.duration && (<span>ห้อง: {item.duration.toUpperCase()}</span>)}
                    </div>
                    <div style={{ fontSize: 13, color: '#888' }}>
                      {item.type && (<span>ประเภท: {item.type.toUpperCase()}</span>)}
                    </div>
                    <div style={{ fontSize: 15, color: '#1976d2', fontWeight: 700 }}>
                      รายได้ทั้งหมด: $
                      {item.result === 'win' && typeof item.amount === 'number' && typeof item.profitPercent === 'number'
                        ? (item.amount + (item.amount * item.profitPercent / 100)).toLocaleString(undefined, { maximumFractionDigits: 2 })
                        : item.amount?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      USD
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        background: item.result === 'win' ? '#e6f9ed' : item.result === 'loss' ? '#fdeaea' : '#fffbe6',
                        color: item.result === 'win' ? '#16b364' : item.result === 'loss' ? '#e53935' : '#bfa100',
                        fontWeight: 600,
                        fontSize: 14,
                        borderRadius: 8,
                        padding: '2px 14px',
                        display: 'inline-block',
                      }}>{tradeStatusBadge.text}</span>
                      {item.result === 'win' && item.profitPercent && (
                        <span style={{ fontSize: 15, color: '#16b364', fontWeight: 700 }}>
                          +{item.profitPercent.toFixed(2)}%
                        </span>
                      )}
                    </div>
                    {typeof item.amount === 'number' && (
                      <div style={{ marginTop: 4, padding: '8px 14px', background: '#f6fcf8', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', boxShadow: '0 1px 2px rgba(22,179,100,0.04)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
                          <FaMoneyBillAlt style={{ color: '#16b364', fontSize: 17 }} />
                          <span style={{ color: '#16b364', fontWeight: 700, fontSize: 15, letterSpacing: 0.2 }}>ต้นทุน</span>
                        </div>
                        <span style={{ color: '#16b364', fontWeight: 700, fontSize: 18, marginLeft: 24, marginTop: 1, letterSpacing: 0.3 }}>
                          {item.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} USD
                        </span>
                      </div>
                    )}
                  </motion.div>
                );
              } else {
                // Render transaction item
                const badge = getStatusBadge(item.status);
                return (
                  <motion.div
                    key={`transaction-${item.id}`}
                    className="transaction-item"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="transaction-icon">
                      {getTypeIcon(item.type)}
                    </div>
                    <div className="transaction-info">
                      <h3>{getTypeText(item.type)}</h3>
                      <p className="transaction-date">
                        {new Date(item.createdAt).toLocaleDateString('th-TH', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="transaction-amount">
                      <p className={`amount ${item.type === 'withdraw' ? 'negative' : 'positive'}`}>
                        {item.type === 'withdraw' ? '-' : '+'}${item.amount?.toLocaleString()}
                      </p>
                      <span className={`badge ${badge.class}`}>{badge.text}</span>
                    </div>
                  </motion.div>
                );
              }
            })}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <Link to="/" className="nav-item">
          <FaHome />
          <span>Home</span>
        </Link>
        <Link to="/trading" className="nav-item">
          <FaChartBar />
          <span>Trade</span>
        </Link>
        <Link to="/history" className="nav-item active">
          <FaEye />
          <span>History</span>
        </Link>
        <Link to="/profile" className="nav-item">
          <FaFileAlt />
          <span>Profile</span>
        </Link>
      </nav>
    </div>
  );
};

export default TransactionHistory;
